import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { Box, Card, Divider, Typography, Tabs, Tab, Chip } from '@mui/material';
import { Person, CalendarToday } from '@mui/icons-material';
import DataEntryModalForBehavior from '../../../../DataEntryModalForBehavior';
import FullScreenQuickTrackDetails from '../../../../FullScreenQuickTrackDetails';

const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role='tabpanel'
      id={`quicktrack-tabpanel-${index}`}
      aria-labelledby={`quicktrack-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      <Box>{children}</Box>
    </div>
  );
};

const QuickTrackTabsSection = forwardRef(
  (
    {
      selectedQuickTracks,
      selectedTabIndex,
      onTabChange,
      user,
      userName,
      originalQuickTrackData,
      dataEntryRef,
      studentBehaviorData,
      formState,
      registerModalData,
      draftEntries = {},
      onUpsertDraft,
      onRemoveDraft,
    },
    ref
  ) => {
    // Create refs for each quick track modal
    const quickTrackModalRefs = useRef({});

    const [fullScreenQuickTrackModal, setFullScreenQuickTrackModal] = useState({
      open: false,
      behavior: null,
      teachersForStudent: [],
    });

    const handleOpenQuickTrackDetailsModal = (track) => {
      if (!track?.behavior) return;
      setFullScreenQuickTrackModal({
        open: true,
        behavior: track.behavior,
        teachersForStudent: track.behavior?.teachersForStudent || [],
      });
    };

    const handleCloseQuickTrackDetailsModal = () => {
      setFullScreenQuickTrackModal({
        open: false,
        behavior: null,
        teachersForStudent: [],
      });
    };

    // Expose saveAll method via ref
    useImperativeHandle(
      ref,
      () => ({
        saveAllQuickTracks: async () => {
          const results = [];
          const errors = [];

          for (const track of selectedQuickTracks) {
            const modalRef = quickTrackModalRefs.current[track.id];
            if (modalRef && typeof modalRef.save === 'function') {
              try {
                const result = await modalRef.save();
                results.push(result);
              } catch (error) {
                errors.push({ trackId: track.id, error });
              }
            }
          }

          if (errors.length > 0) {
            throw new Error(`Failed to save ${errors.length} quick track(s)`);
          }

          return results;
        },
      }),
      [selectedQuickTracks]
    );

    // Early return if no tracks - component stays mounted
    if (selectedQuickTracks.length === 0) {
      return null;
    }

    const handleGetEditingDataPoint = (quickTrackData) => {
      if (!quickTrackData) return null;
      const quickTrackID = quickTrackData?.quickTrackID;
      const dataEntryID = quickTrackData?.dataEntryID;
      if (!quickTrackID || !dataEntryID) return null;
      const studentId = user?.studentId || user?.id || formState?.studentId;
      if (!studentId) return null;
      const studentQuickTracks = studentBehaviorData?.find(
        (s) => s.id === studentId
      );
      if (!studentQuickTracks) return null;
      const quickTrack = studentQuickTracks?.behaviors?.find(
        (b) => b.id === quickTrackID
      );
      return quickTrack?.goalData?.find((g) => g.id === dataEntryID);
    };

    return (
      <Box sx={{ px: 3, pb: 3 }}>
        <Divider sx={{ mb: 3 }} />

        {/* Selected Quick Tracks Chips - Deletable */}
        <Typography
          variant='subtitle1'
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontWeight: 600,
            color: '#2c3e50',
            mb: 2,
          }}
        >
          Selected QuickTracks for Data Entry
        </Typography>

        {/* Tabbed Data Entry for Each Selected Quick Track */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid #e9ecef',
          }}
        >
          <Box
            sx={{
              borderBottom: '1px solid #e9ecef',
              backgroundColor: '#f8f9ff',
            }}
          >
            <Tabs
              value={selectedTabIndex}
              onChange={onTabChange}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                '& .MuiTab-root': {
                  fontFamily: 'Lato, sans-serif',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: '#666',
                  '&.Mui-selected': {
                    color: '#9291ef',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#9291ef',
                },
              }}
            >
              {selectedQuickTracks.map((track, index) => (
                <Tab
                  key={track.id}
                  label={track.name}
                  id={`quicktrack-tab-${index}`}
                  aria-controls={`quicktrack-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>

          {selectedQuickTracks.map((track, index) => {
            const currentDate = formState?.date || formatDate(new Date());
            const entryKey = `${track.id}-${currentDate}`;
            const canViewQuickTrackDetails = Boolean(track?.behavior?.info);

            return (
              <TabPanel key={track.id} value={selectedTabIndex} index={index}>
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor:
                        'linear-gradient(135deg, #f8f9ff 0%, #f0f1ff 100%)',
                      borderRadius: 2,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        icon={<Person sx={{ fontSize: 18 }} />}
                        label={userName || user?.name || user?.fullname}
                        variant='outlined'
                        sx={{
                          fontWeight: 600,
                          bgcolor: 'white',
                          border: '1px solid #9291ef',
                          color: '#9291ef',
                          fontSize: '0.9rem',
                          height: 36,
                          '& .MuiChip-icon': {
                            color: '#9291ef',
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      {canViewQuickTrackDetails && (
                        <Chip
                          label={'View QuickTrack Details'}
                          onClick={() => handleOpenQuickTrackDetailsModal(track)}
                          variant='filled'
                          sx={{
                            fontWeight: 600,
                            bgcolor: '#9291ef',
                            color: 'white',
                            fontSize: '0.9rem',
                            height: 36,
                            marginRight: 1,
                          }}
                        />
                      )}
                      <Chip
                        icon={<CalendarToday sx={{ fontSize: 18 }} />}
                        label={currentDate}
                        variant='outlined'
                        sx={{
                          fontWeight: 600,
                          bgcolor: 'white',
                          border: '1px solid #9291ef',
                          color: '#9291ef',
                          fontSize: '0.9rem',
                          height: 36,
                          '& .MuiChip-icon': {
                            color: '#9291ef',
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e9ecef',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    }}
                  >
                    <DataEntryModalForBehavior
                      ref={(el) => {
                        if (el) {
                          quickTrackModalRefs.current[track.id] = el;
                        }
                      }}
                      open={true}
                      onClose={() => {}}
                      selectedDate={currentDate}
                      behavior={{
                        ...track.behavior,
                        id: track.id,
                        docId: track.id,
                      }}
                      selectedDataPoint={handleGetEditingDataPoint(
                        originalQuickTrackData?.find(
                          (t) => t.quickTrackID === track.id
                        )
                      )}
                      onSuccess={() => {}}
                      fromSessionNotes={true}
                      dataEntryRef={dataEntryRef}
                      entryKey={entryKey}
                      registerModalData={registerModalData}
                      draftState={draftEntries[track.id]}
                      onDraftChange={(draft) => {
                        if (typeof onUpsertDraft === 'function') {
                          onUpsertDraft(track.id, draft);
                        }
                      }}
                      onDraftClear={() => {
                        if (typeof onRemoveDraft === 'function') {
                          onRemoveDraft(track.id);
                        }
                      }}
                    />
                  </Box>
                </Box>
              </TabPanel>
            );
          })}
        </Card>
        <FullScreenQuickTrackDetails
          open={fullScreenQuickTrackModal.open}
          onClose={handleCloseQuickTrackDetailsModal}
          behavior={fullScreenQuickTrackModal.behavior || {}}
          teachersForStudent={fullScreenQuickTrackModal.teachersForStudent}
        />
      </Box>
    );
  }
);

export default QuickTrackTabsSection;
